/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of term data loaded from the ServiceStack API.
 */
import { TermSelectors, useTermSelector, TermActions } from 'src/store/reducers/term';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useTerm = (
    termId: number
): [
    Dtos.Version | null,
    Dtos.Category | null,
    Dtos.Term | null,
    Dtos.Term | null,
    IRequestState
] => {
    const version = useTermSelector(state => TermSelectors.selectTermVersion(state, termId));
    const category = useTermSelector(state => TermSelectors.selectTermCategory(state, termId));
    const parentTerm = useTermSelector(state => TermSelectors.selectTermParent(state, termId));
    const term = useTermSelector(state => TermSelectors.selectTermTerm(state, termId));
    const loadState = useTermSelector(state => TermSelectors.selectTermLoadState(state, termId));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(TermActions.loadById(termId));

        return () => {
            dispatch(TermActions.clear(termId));
        }
    }, [termId, dispatch]);

    return [
        version,
        category,
        parentTerm,
        term,
        loadState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useTerm;


// WEBPACK FOOTER //
// ./src/hooks/useTerm.ts